import React from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Stack,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import 'dayjs/locale/pl';
import { InvoiceImportStatus } from '../Types/invoiceImport.types';

export interface ImportHistoryFiltersState {
  status: InvoiceImportStatus | null;
  searchTerm: string;
  importerType: string;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
}

interface ImportHistoryFiltersProps {
  filters: ImportHistoryFiltersState;
  onFilterChange: (filters: ImportHistoryFiltersState) => void;
  onClear: () => void;
}

const STATUS_OPTIONS = [
  { value: '', label: 'Wszystkie' },
  { value: InvoiceImportStatus.Pending, label: 'Oczekujące' },
  { value: InvoiceImportStatus.Processing, label: 'Przetwarzane' },
  { value: InvoiceImportStatus.Completed, label: 'Zakończone' },
  { value: InvoiceImportStatus.Failed, label: 'Nieudane' },
  { value: InvoiceImportStatus.Duplicate, label: 'Duplikat' },
  { value: InvoiceImportStatus.RequiresReview, label: 'Wymaga weryfikacji' },
];

const IMPORTER_TYPE_OPTIONS = [
  { value: '', label: 'Wszystkie' },
  { value: 'DummyImporter', label: 'DummyImporter (Test)' },
  { value: 'KSeFXmlImporter', label: 'KSeF' },
  { value: 'OcrImporter', label: 'OCR' },
  { value: 'CsvImporter', label: 'CSV' },
];

/**
 * Filter controls for import history
 * Status, filename search, importer type, date range
 */
const ImportHistoryFilters: React.FC<ImportHistoryFiltersProps> = ({
  filters,
  onFilterChange,
  onClear,
}) => {
  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onFilterChange({
      ...filters,
      status: value ? (value as InvoiceImportStatus) : null,
    });
  };

  const handleFileNameSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({
      ...filters,
      searchTerm: event.target.value,
    });
  };

  const handleImporterTypeChange = (event: SelectChangeEvent<string>) => {
    onFilterChange({
      ...filters,
      importerType: event.target.value,
    });
  };

  const handleStartDateChange = (date: Dayjs | null) => {
    onFilterChange({
      ...filters,
      fromDate: date,
    });
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    onFilterChange({
      ...filters,
      toDate: date,
    });
  };

  const hasActiveFilters =
    filters.status ||
    filters.searchTerm ||
    filters.importerType ||
    filters.fromDate ||
    filters.toDate;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Stack spacing={2}>
          {/* Row 1: Status + Importer Type */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={filters.status || ''}
                label="Status"
                onChange={handleStatusChange}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="importer-type-filter-label">Typ importera</InputLabel>
              <Select
                labelId="importer-type-filter-label"
                id="importer-type-filter"
                value={filters.importerType || ''}
                label="Typ importera"
                onChange={handleImporterTypeChange}
              >
                {IMPORTER_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Row 2: Date Range + Filename Search */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <DatePicker
              label="Data od"
              value={filters.fromDate}
              onChange={handleStartDateChange}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />

            <DatePicker
              label="Data do"
              value={filters.toDate}
              onChange={handleEndDateChange}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />

            <TextField
              fullWidth
              label="Nazwa pliku"
              placeholder="Szukaj po nazwie pliku..."
              value={filters.searchTerm}
              onChange={handleFileNameSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          {/* Row 3: Clear button */}
          {hasActiveFilters && (
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<ClearIcon />}
                onClick={onClear}
              >
                Wyczyść filtry
              </Button>
            </Box>
          )}
        </Stack>
      </Box>
    </LocalizationProvider>
  );
};

export default ImportHistoryFilters;
